import ky from "ky";
import type { AuditLog, Device, RedisKey, Stats, Scale, PickToLight, InkjetPrinter, DataManCamera, BarcodeReading, DataManCameraStats } from "../types";

const API_BASE_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:8051"
    : "https://carta-scale-api.techvibe.vn";

export const api = ky.create({
  prefixUrl: API_BASE_URL,
  timeout: 30000,
  credentials: "include", // Important: This allows cookies to be sent with requests
  hooks: {
    afterResponse: [
      async (request, options, response) => {
        if (
          response.status === 401 &&
          !request.url.includes("admin/auth/status")
        ) {
          window.location.href = "/login";
        }

        if (!response.ok) {
          const error = await response
            .json()
            .catch(() => ({ message: "Unknown error" }));
          throw new Error(
            (error as any).message || `HTTP error! status: ${response.status}`
          );
        }
      },
    ],
  },
});

export const apiClient = {
  // Auth endpoints
  login: (credentials: { username: string; password: string }) =>
    api
      .post("admin/login", { json: credentials })
      .json<{ success: boolean; user: any }>(),

  logout: () => api.post("admin/logout"),

  // Device endpoints
  getDevices: () =>
    api.get("admin/devices").json<{ tcp: Device[]; mqtt: Device[] }>(),

  getTCPDevices: () => api.get("admin/devices/tcp").json<Device[]>(),

  getMQTTDevices: () => api.get("admin/devices/mqtt").json<Device[]>(),

  // Stats endpoints
  getStats: () => api.get("admin/stats").json<Stats>(),

  // Audit logs
  getAuditLogs: () => api.get("admin/audit-logs").json<AuditLog[]>(),

  // Redis keys
  getRedisKeys: () => api.get("admin/redis/keys").json<RedisKey[]>(),

  getRedisKey: (key: string) =>
    api.get(`admin/redis/keys/${key}`).json<RedisKey>(),

  // Scale endpoints
  getScales: () => api.get("admin/scales").json<Scale[]>(),
  createScale: (data: Partial<Scale>) =>
    api.post("admin/scales", { json: data }).json<Scale>(),
  getScale: (id: string) => api.get(`admin/scales/${id}`).json<Scale>(),
  updateScale: (id: string, data: Partial<Scale>) =>
    api.put(`admin/scales/${id}`, { json: data }).json<Scale>(),
  deleteScale: (id: string) => api.delete(`admin/scales/${id}`),

  // PickToLight endpoints
  getPickToLights: () => api.get("admin/pick-to-lights").json<PickToLight[]>(),
  createPickToLight: (data: Partial<PickToLight>) =>
    api.post("admin/pick-to-lights", { json: data }).json<PickToLight>(),
  getPickToLight: (id: string) =>
    api.get(`admin/pick-to-lights/${id}`).json<PickToLight>(),
  updatePickToLight: (id: string, data: Partial<PickToLight>) =>
    api.put(`admin/pick-to-lights/${id}`, { json: data }).json<PickToLight>(),
  deletePickToLight: (id: string) => api.delete(`admin/pick-to-lights/${id}`),

  // InkjetPrinter endpoints
  getInkjetPrinters: () =>
    api.get("admin/inkjet-printers").json<InkjetPrinter[]>(),
  createInkjetPrinter: (data: Partial<InkjetPrinter>) =>
    api.post("admin/inkjet-printers", { json: data }).json<InkjetPrinter>(),
  getInkjetPrinter: (id: string) =>
    api.get(`admin/inkjet-printers/${id}`).json<InkjetPrinter>(),
  updateInkjetPrinter: (id: string, data: Partial<InkjetPrinter>) =>
    api
      .put(`admin/inkjet-printers/${id}`, { json: data })
      .json<InkjetPrinter>(),
  deleteInkjetPrinter: (id: string) =>
    api.delete(`admin/inkjet-printers/${id}`),

  // DataMan Camera endpoints (api/v1 returns { code, data, msg } wrapper)
  getDataManCameras: () =>
    api.get("api/v1/dataman-cameras").json<{ data: DataManCamera[] }>().then(r => r.data),
  createDataManCamera: (data: { device_id: string; name: string; host: string; port: number }) =>
    api.post("api/v1/dataman-cameras", { json: data }).json<{ data: DataManCamera }>().then(r => r.data),
  getDataManCamera: (id: string) =>
    api.get(`api/v1/dataman-cameras/${id}`).json<{ data: DataManCamera }>().then(r => r.data),
  updateDataManCamera: (id: string, data: Partial<{ name: string; host: string; port: number }>) =>
    api.put(`api/v1/dataman-cameras/${id}`, { json: data }).json<{ data: DataManCamera }>().then(r => r.data),
  deleteDataManCamera: (id: string) =>
    api.delete(`api/v1/dataman-cameras/${id}`),

  // DataMan Barcode readings
  getCameraReadings: (cameraId: string, page = 1, pageSize = 50) =>
    api.get(`api/v1/dataman-cameras/${cameraId}/readings`, {
      searchParams: { page, page_size: pageSize },
    }).json<{ data: { items: BarcodeReading[]; pagination: { page: number; per_page: number; total: number; total_pages: number } } }>()
      .then(r => ({ data: r.data.items || [], meta: r.data.pagination })),
  getLatestReadings: (cameraId: string, limit = 10) =>
    api.get(`api/v1/dataman-cameras/${cameraId}/readings/latest`, {
      searchParams: { limit },
    }).json<{ data: BarcodeReading[] }>().then(r => r.data),
  getLatestSingleReading: (cameraId: string) =>
    api.get(`api/v1/dataman-cameras/${cameraId}/readings/latest-single`).json<{ data: BarcodeReading }>().then(r => r.data),
  getBarcodeReading: (id: string) =>
    api.get(`api/v1/barcode-readings/${id}`).json<{ data: BarcodeReading }>().then(r => r.data),
  getCameraStatistics: (cameraId: string) =>
    api.get(`api/v1/dataman-cameras/${cameraId}/statistics`).json<{ data: DataManCameraStats }>().then(r => r.data),
  triggerCamera: (cameraId: string) =>
    api.post(`api/v1/dataman-cameras/${cameraId}/trigger`).json<any>(),
};
